'use client'

import { FilterContext, getFilterId } from '../../../../providers/filter-provider'
import { FilterOperator, FilterValue } from '../../../../types/Filters'
import { useContext, useEffect, useState } from 'react'

/**
 * Hook that can be used by default filters, i.e. filters on a string. Registers the filter and handles updates to the filter and its value.
 * @param filterName Name of the filter, used in creating a unique ID for the filter
 * @param operator The operator used to match the filter with the set of data
 * @param initialValue Initial value of the filter
 */
export const useDefaultFilter = (filterName: string, operator: FilterOperator, initialValue?: FilterValue) => {
    const { filters, registerFilter, updateFilterValue, getFilterValue, getFilterById } = useContext(FilterContext)
    const filterId = getFilterId(filterName, operator)
    const [value, setValue] = useState<FilterValue>(getFilterValue(filterId))

    useEffect(() => {
        registerFilter({
            id: filterId,
            key: filterName,
            operator: operator,
            value: getFilterValue(filterId) ?? initialValue,
        })
    }, [])

    /**
     * Update the filter value when
     *  - the value updates and
     *  - differs from the filter value
     **/
    const handleValueUpdate = () => {
        const filter = getFilterById(filterId)

        if (filter && filter.value !== value) {
            updateFilterValue({ filter: filter, newValue: value })
        }
    }

    /**
     * Update the selected value when
     *  - filter exists and
     *  - selectedValue differs from the filter value
     */
    const handleFilterUpdate = () => {
        const filter = getFilterById(filterId)
        if (filter) {
            const matchingFilter = filters.find((stateFilter) => stateFilter.id === filter.id)
            if (matchingFilter && value !== matchingFilter?.value) {
                setValue(matchingFilter.value)
            }
        }
    }

    useEffect(() => {
        handleFilterUpdate()
    }, [filters])

    useEffect(() => {
        handleValueUpdate()
    }, [value])

    const fieldName = `default-filter_${filterName}[${operator}]`

    return {
        fieldName,
        value,
        setValue,
    }
}
